import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart2, Newspaper, Brain, Target, Trophy } from 'lucide-react';
import { getBrazilianStatus } from '../../utils/detectBrazil';
import { gamePredictionModalPtBR } from '../mappings/gamePredictionModal.pt-br';

// Premium glass container component
const GlassContainer = ({ children, className = "" }) => (
  <div className={`relative backdrop-blur-sm bg-white/[0.02] border border-white/[0.05] rounded-lg ${className}`}>
    <div className="relative z-10 h-full flex flex-col">{children}</div>
  </div>
);

// Clean connection network visualization
const ConnectionNetwork = ({ isActive = false }) => {
  const nodes = useMemo(() => [
    { id: 1, x: 50, y: 30 },
    { id: 2, x: 30, y: 50 },
    { id: 3, x: 70, y: 50 },
    { id: 4, x: 50, y: 70 },
    { id: 5, x: 20, y: 70 },
    { id: 6, x: 80, y: 70 }
  ], []);

  const connections = useMemo(() => [
    { from: nodes[0], to: nodes[1] },
    { from: nodes[0], to: nodes[2] },
    { from: nodes[1], to: nodes[3] },
    { from: nodes[2], to: nodes[3] },
    { from: nodes[1], to: nodes[4] },
    { from: nodes[2], to: nodes[5] }
  ], [nodes]);

  return (
    <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 100 100">
      {/* Connections */}
      {connections.map((conn, i) => (
        <motion.line
          key={i}
          x1={conn.from.x}
          y1={conn.from.y}
          x2={conn.to.x}
          y2={conn.to.y}
          stroke="url(#connectionGradient)"
          strokeWidth="1.5"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ 
            pathLength: isActive ? 1 : 0.3,
            opacity: isActive ? 0.8 : 0.2,
          }}
          transition={{ 
            duration: 1.5,
            delay: i * 0.2
          }}
        />
      ))}
      
      {/* Nodes */}
      {nodes.map((node, i) => (
        <motion.circle
          key={node.id}
          cx={node.x}
          cy={node.y}
          r="2"
          fill="url(#nodeGradient)"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ 
            scale: isActive ? 1 : 0.5,
            opacity: isActive ? 0.9 : 0.4
          }}
          transition={{ 
            duration: 0.8,
            delay: i * 0.1
          }}
        />
      ))}
      
      <defs>
        <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.3" />
        </linearGradient>
        <radialGradient id="nodeGradient">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#059669" stopOpacity="0.6" />
        </radialGradient>
      </defs>
    </svg>
  );
};

// Clean data flow visualization
const DataFlowVisualization = ({ isActive }) => {
  return (
    <div className="absolute inset-0 rounded-xl overflow-hidden">
      <div className="absolute inset-0">
        {/* Horizontal data streams */}
        {Array.from({ length: 6 }).map((_, i) => (
          <motion.div
            key={`h-stream-${i}`}
            className="absolute h-px bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent"
            style={{ 
              top: `${20 + i * 12}%`,
              width: '70%',
              left: '15%'
            }}
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{
              scaleX: isActive ? 1 : 0,
              opacity: isActive ? 0.6 : 0,
            }}
            transition={{
              duration: 1.2,
              delay: i * 0.2,
              ease: "easeOut"
            }}
          />
        ))}
        
        {/* Vertical connectors */}
        {Array.from({ length: 4 }).map((_, i) => (
          <motion.div
            key={`v-stream-${i}`}
            className="absolute w-px bg-gradient-to-b from-transparent via-violet-400/30 to-transparent"
            style={{ 
              left: `${25 + i * 18}%`,
              height: '60%',
              top: '20%'
            }}
            initial={{ scaleY: 0, opacity: 0 }}
            animate={{
              scaleY: isActive ? 1 : 0,
              opacity: isActive ? 0.5 : 0,
            }}
            transition={{
              duration: 1.2,
              delay: (i * 0.3) + 0.5,
              ease: "easeOut"
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Premium progress indicator
const CleanProgressIndicator = ({ steps, currentStep, microProgress = 0 }) => {
  const totalProgress = Math.min((currentStep + microProgress) / steps.length, 1);

  return (
    <div className="w-full space-y-4">
      {/* Step indicators */}
      <div className="flex items-center justify-center space-x-4">
        {steps.map((step, index) => (
          <div key={index} className="relative flex items-center">
            {/* Connection line */}
            {index < steps.length - 1 && (
              <motion.div
                className="absolute left-8 w-8 h-px bg-white/[0.05]"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: index < currentStep ? 1 : 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              />
            )}

            {/* Step circle */}
            <motion.div
              className={`relative w-8 h-8 rounded-full border flex items-center justify-center z-10 backdrop-blur-sm transition-all duration-200
                ${index < currentStep
                  ? 'border-emerald-500/20 bg-emerald-400/20'
                  : index === currentStep
                    ? 'border-emerald-500/30 bg-emerald-400/30'
                    : 'border-white/[0.05] bg-white/[0.02]'
                }`}
              animate={{
                scale: index === currentStep ? 1.1 : 1,
              }}
              transition={{ duration: 0.3 }}
            >
              <step.icon className={`w-4 h-4 ${index <= currentStep ? 'text-emerald-400/80' : 'text-white/30'}`} />
            </motion.div>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div className="relative">
        <div className="h-2 bg-white/[0.02] rounded-full overflow-hidden backdrop-blur-sm border border-white/[0.05]">
          <motion.div
            className="h-full bg-gradient-to-r from-emerald-400/80 to-violet-400/80 relative overflow-hidden"
            initial={{ width: "0%" }}
            animate={{ width: `${totalProgress * 100}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {/* Subtle shimmer effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent w-8"
              animate={{ x: [-32, 200] }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.div>
        </div>

        {/* Progress percentage */}
        <div className="flex justify-between mt-2 text-xs">
          <span className="text-white/50 uppercase tracking-wider font-mono text-[10px]">Processing</span>
          <span className="text-emerald-400/80 font-mono text-[11px]">
            {Math.floor(totalProgress * 100)}%
          </span>
        </div>
      </div>
    </div>
  );
};

// Base Analysis Component - Completely redesigned
const BaseAnalysisComponent = ({ 
  predictionState, 
  displayStep, 
  steps,
  remainingSpins,
  isBasicUser,
  onClose,
  navigate,
  setShowResetInfo
}) => {
  const [microStepIndex, setMicroStepIndex] = useState(0);
  
  // Check if user is Brazilian
  const isBrazilian = getBrazilianStatus();
  
  // Translation function
  const t = (text) => {
    if (!isBrazilian) return text;
    return gamePredictionModalPtBR[text] || text;
  };
  
  // Enhanced steps with microsteps
  const enhancedSteps = useMemo(() => steps.map((step, index) => ({
    ...step,
    microsteps: [
      `${step.text}...`,
      `Processing ${step.text.toLowerCase()}...`,
      `Analyzing patterns...`,
      `Finalizing results...`
    ]
  })), [steps]);

  useEffect(() => {
    if (predictionState.step < 3 && enhancedSteps[displayStep]?.microsteps?.length) {
      const interval = setInterval(() => {
        setMicroStepIndex(prev => 
          (prev + 1) % enhancedSteps[displayStep].microsteps.length
        );
      }, 2000);
      
      return () => clearInterval(interval);
    }
    setMicroStepIndex(0);
  }, [displayStep, enhancedSteps, predictionState.step]);

  const microProgress = microStepIndex / (enhancedSteps[displayStep]?.microsteps?.length || 1);

  if (predictionState.step < 3) {
    return (
      <div className="flex flex-col items-center justify-center space-y-5 p-4">
        {/* Premium visualization container */}
        <div className="w-48 h-48">
          <GlassContainer className="w-full h-full">
            {/* Background elements as overlay */}
            <div className="absolute inset-0 z-0 opacity-20">
              <ConnectionNetwork isActive={predictionState.step > 0} />
              <DataFlowVisualization isActive={predictionState.step > 1} />
            </div>

            {/* Main content area with explicit vertical layout */}
            <div className="flex flex-col h-full justify-center items-center">
              {/* Icon container positioned using flex layout instead of absolute */}
              <div className="mb-4">
                <div className="relative">
                  <div className="relative w-28 h-28 bg-white/[0.02] rounded-md flex items-center justify-center backdrop-blur-sm border border-white/[0.05] mt-10">
                    {(() => {
                      const StepIcon = enhancedSteps[displayStep].icon;
                      return (
                        <StepIcon className="w-14 h-14 text-emerald-400/80" />
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </GlassContainer>
        </div>

        {/* Content section */}
        <div className="text-center w-full max-w-md space-y-6">
          {/* Main step text */}
          <motion.div
            key={displayStep}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-2"
          >
            <h3 className="text-lg font-medium tracking-wider text-white/80 uppercase">
              {enhancedSteps[displayStep].text}
            </h3>

            {/* Microstep indicator */}
            <AnimatePresence mode="wait">
              <motion.p
                key={`${displayStep}-${microStepIndex}`}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.4 }}
                className="text-xs text-white/50 h-6 font-mono"
              >
                {enhancedSteps[displayStep]?.microsteps?.[microStepIndex] || ''}
              </motion.p>
            </AnimatePresence>
          </motion.div>

          {/* Premium progress indicator */}
          <CleanProgressIndicator
            steps={enhancedSteps}
            currentStep={displayStep}
            microProgress={microProgress}
          />
        </div>
      </div>
    );
  } else {
    // Results ready with premium completion
    return (
      <div className="space-y-8 p-6">
        <div className="text-center space-y-6">
          <motion.div
            initial={{ scale: 0, rotate: -90 }}
            animate={{
              scale: 1,
              rotate: 0,
            }}
            transition={{
              type: "spring",
              duration: 0.8,
              damping: 15
            }}
            className="relative"
          >
            <div className="relative text-8xl">🏆</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-3"
          >
            <h3 className="text-xl font-medium tracking-wider text-white/80 uppercase">
              {t('Prediction Complete')}
            </h3>
            <p className="text-white/50 text-sm">
              {t('Your comprehensive analysis is ready to view')}
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex justify-center gap-4"
        >
          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-8 py-3 bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08] rounded-md transition-all duration-200 text-white/70 hover:text-white/80 text-xs uppercase tracking-wider backdrop-blur-sm"
          >
            {t('Close')}
          </motion.button>

          {remainingSpins === 0 && (
            isBasicUser ? (
              <motion.button
                onClick={() => navigate('/pricing')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80 hover:from-emerald-500 hover:to-violet-500 rounded-md transition-all duration-200 text-white text-xs uppercase tracking-wider"
              >
                {t('Upgrade for More Spins')}
              </motion.button>
            ) : (
              <motion.button
                onClick={() => setShowResetInfo(true)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-3 bg-white/[0.02] border border-violet-500/20 hover:bg-white/[0.03] hover:border-violet-500/30 rounded-md transition-all duration-200 text-violet-400/70 hover:text-violet-400/90 text-xs uppercase tracking-wider"
              >
                {t('Spin Reset Info')}
              </motion.button>
            )
          )}
        </motion.div>
      </div>
    );
  }
};

// All sport components using the clean BaseAnalysisComponent
export const MLBPredictionComponent = ({ 
  predictionState, 
  displayStep, 
  steps,
  remainingSpins,
  isBasicUser,
  onClose, 
  navigate,
  setShowResetInfo
}) => (
  <BaseAnalysisComponent
    predictionState={predictionState}
    displayStep={displayStep}
    steps={steps}
    remainingSpins={remainingSpins}
    isBasicUser={isBasicUser}
    onClose={onClose}
    navigate={navigate}
    setShowResetInfo={setShowResetInfo}
  />
);

export const NBAPredictionComponent = ({ 
  predictionState, 
  displayStep, 
  steps,
  remainingSpins,
  isBasicUser,
  onClose, 
  navigate,
  setShowResetInfo
}) => (
  <BaseAnalysisComponent
    predictionState={predictionState}
    displayStep={displayStep}
    steps={steps}
    remainingSpins={remainingSpins}
    isBasicUser={isBasicUser}
    onClose={onClose}
    navigate={navigate}
    setShowResetInfo={setShowResetInfo}
  />
);

export const NHLPredictionComponent = ({ 
  predictionState, 
  displayStep, 
  steps,
  remainingSpins,
  isBasicUser,
  onClose, 
  navigate,
  setShowResetInfo
}) => (
  <BaseAnalysisComponent
    predictionState={predictionState}
    displayStep={displayStep}
    steps={steps}
    remainingSpins={remainingSpins}
    isBasicUser={isBasicUser}
    onClose={onClose}
    navigate={navigate}
    setShowResetInfo={setShowResetInfo}
  />
);

export const SoccerPredictionComponent = ({ 
  predictionState, 
  displayStep, 
  steps,
  remainingSpins,
  isBasicUser,
  onClose, 
  navigate,
  setShowResetInfo
}) => (
  <BaseAnalysisComponent
    predictionState={predictionState}
    displayStep={displayStep}
    steps={steps}
    remainingSpins={remainingSpins}
    isBasicUser={isBasicUser}
    onClose={onClose}
    navigate={navigate}
    setShowResetInfo={setShowResetInfo}
  />
);

export const NCAABaseballPredictionComponent = ({ 
  predictionState, 
  displayStep, 
  steps,
  remainingSpins,
  isBasicUser,
  onClose, 
  navigate,
  setShowResetInfo
}) => (
  <BaseAnalysisComponent
    predictionState={predictionState}
    displayStep={displayStep}
    steps={steps}
    remainingSpins={remainingSpins}
    isBasicUser={isBasicUser}
    onClose={onClose}
    navigate={navigate}
    setShowResetInfo={setShowResetInfo}
  />
);

export const NBAFinalsPredictionComponent = ({ 
  predictionState, 
  displayStep, 
  steps,
  remainingSpins,
  isBasicUser,
  onClose, 
  navigate,
  setShowResetInfo
}) => (
  <BaseAnalysisComponent
    predictionState={predictionState}
    displayStep={displayStep}
    steps={steps}
    remainingSpins={remainingSpins}
    isBasicUser={isBasicUser}
    onClose={onClose}
    navigate={navigate}
    setShowResetInfo={setShowResetInfo}
  />
);

export const MLSPredictionComponent = ({ 
  predictionState, 
  displayStep, 
  steps,
  remainingSpins,
  isBasicUser,
  onClose, 
  navigate,
  setShowResetInfo
}) => (
  <BaseAnalysisComponent
    predictionState={predictionState}
    displayStep={displayStep}
    steps={steps}
    remainingSpins={remainingSpins}
    isBasicUser={isBasicUser}
    onClose={onClose}
    navigate={navigate}
    setShowResetInfo={setShowResetInfo}
  />
);

export const BrasileirãoPredictionComponent = ({ 
  predictionState, 
  displayStep, 
  steps,
  remainingSpins,
  isBasicUser,
  onClose, 
  navigate,
  setShowResetInfo
}) => (
  <BaseAnalysisComponent
    predictionState={predictionState}
    displayStep={displayStep}
    steps={steps}
    remainingSpins={remainingSpins}
    isBasicUser={isBasicUser}
    onClose={onClose}
    navigate={navigate}
    setShowResetInfo={setShowResetInfo}
  />
);

export const NFLPredictionComponent = ({ 
  predictionState, 
  displayStep, 
  steps,
  remainingSpins,
  isBasicUser,
  onClose, 
  navigate,
  setShowResetInfo
}) => (
  <BaseAnalysisComponent
    predictionState={predictionState}
    displayStep={displayStep}
    steps={steps}
    remainingSpins={remainingSpins}
    isBasicUser={isBasicUser}
    onClose={onClose}
    navigate={navigate}
    setShowResetInfo={setShowResetInfo}
  />
);

export const NCAAFPredictionComponent = ({ 
  predictionState, 
  displayStep, 
  steps,
  remainingSpins,
  isBasicUser,
  onClose, 
  navigate,
  setShowResetInfo
}) => (
  <BaseAnalysisComponent
    predictionState={predictionState}
    displayStep={displayStep}
    steps={steps}
    remainingSpins={remainingSpins}
    isBasicUser={isBasicUser}
    onClose={onClose}
    navigate={navigate}
    setShowResetInfo={setShowResetInfo}
  />
);

// Premium Sport Selection Component
export const SportSelectionComponent = ({
  availableSports,
  onSportSelect,
  remainingSpins,
  predictionState
}) => {
  const canSelectSport = remainingSpins > 0 || predictionState?.paidWithStarCoins;

  // Check if user is Brazilian
  const isBrazilian = getBrazilianStatus();

  // Translation function
  const t = (text) => {
    if (!isBrazilian) return text;
    return gamePredictionModalPtBR[text] || text;
  };

  return (
    <div className="grid grid-cols-2 gap-2 sm:gap-4 p-0 sm:p-2">
      {availableSports.map((sport, index) => {
        const isInSeason = sport.inSeason !== false;
        const isSelectable = canSelectSport && isInSeason;

        return (
          <motion.div
            key={sport.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <motion.button
              onClick={() => isSelectable && onSportSelect(sport.name)}
              disabled={!isSelectable}
              whileHover={isSelectable ? {
                scale: 1.02,
                y: -2
              } : {}}
              whileTap={isSelectable ? { scale: 0.98 } : {}}
              className={`w-full p-3 sm:p-5 rounded-lg flex flex-col items-center gap-1 sm:gap-3 border transition-all duration-200 relative overflow-hidden group ${
                isSelectable
                  ? "bg-white/[0.02] backdrop-blur-sm hover:bg-white/[0.03] border-white/[0.05] hover:border-emerald-500/30"
                  : isInSeason && !canSelectSport
                    ? "bg-white/[0.02] cursor-not-allowed opacity-50 border-white/[0.05]"
                    : "bg-white/[0.02] cursor-not-allowed opacity-30 border-white/[0.05]"
              }`}
            >
              {/* Sport icon - removed emoji filter */}
              <div className={`text-3xl sm:text-5xl relative z-10 ${!isInSeason ? 'opacity-30' : ''}`}>
                {sport.icon}
              </div>

              {/* Sport name */}
              <span className={`font-medium text-xs sm:text-sm relative z-10 transition-colors uppercase tracking-wider ${
                isSelectable
                  ? "text-white/70 group-hover:text-emerald-400/80"
                  : !isInSeason
                    ? "text-white/30"
                    : "text-white/50"
              }`}>
                {t(sport.name)}
              </span>

              {/* Out of season indicator */}
              {!isInSeason && (
                <span className="text-[10px] sm:text-xs text-white/30 relative z-10 uppercase tracking-wider font-mono">{t('Out of Season')}</span>
              )}

              {/* Hover indicator */}
              {isSelectable && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-400/80 to-violet-400/80 opacity-0 group-hover:opacity-100"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                />
              )}
            </motion.button>
          </motion.div>
        );
      })}
    </div>
  );
};